import {useEffect, useRef} from 'react'

import {getBuild} from '../../../../../selectors'
import type {BuildId} from '../../../../../types'
import {focusSelfOrChildLink} from '../../../../../utils/dom'
import {useSandbox} from '../../../../../utils/fastdom'
import {escapeFilePathForURL} from '../../../../../utils/url'
import FileTreeNode, {ITEM_SELECTOR} from '../../../BuildArtifactsTree/FileTreeNode/FileTreeNode'

import styles from './DownloadedDeliveredArtifacts.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
  level?: number
  parentPath?: string
  paths: ReadonlyArray<string>
}

type Dir = {
  dirName: string
  children: string[]
}

function extractDirs(paths: ReadonlyArray<string>) {
  const dirs = []
  const dirsHash: Record<string, Dir> = {}
  const files = []

  for (const path of paths) {
    const match = path.match(/^(.*?)\/(.*)$/)
    if (match == null) {
      files.push(path)
    } else {
      const [_, dirName, childPath] = match
      let dir = dirsHash[dirName]
      if (dir == null) {
        dir = dirsHash[dirName] = {dirName, children: []}
        dirs.push(dir)
      }
      dir.children.push(childPath)
    }
  }

  return {dirs, files}
}

export default function Files({parentPath = '', paths, level = 0, buildId}: Props) {
  const {dirs, files} = extractDirs(paths)
  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)
  const ref = useRef<HTMLUListElement>(null)
  const sandbox = useSandbox()

  useEffect(() => {
    if (level === 0) {
      const element = ref.current

      if (element == null) {
        return
      }

      const firstItem = element.querySelector(ITEM_SELECTOR)
      sandbox.mutate(() => {
        const {documentElement} = document
        const prevScroll = documentElement?.scrollTop
        focusSelfOrChildLink(firstItem)

        if (documentElement && prevScroll != null) {
          documentElement.scrollTop = prevScroll
        }
      })
    }
  }, [level, sandbox])

  return (
    <ul ref={ref} role={level === 0 ? 'tree' : 'group'} className={styles.list}>
      {dirs.map(({dirName, children}) => (
        <FileTreeNode key={dirName} level={level} expandable name={dirName} icon="folder">
          <Files
            parentPath={`${parentPath}/${dirName}`}
            paths={children}
            level={level + 1}
            buildId={buildId}
          />
        </FileTreeNode>
      ))}
      {files.map(file => (
        <FileTreeNode
          href={`/repository/download/${buildTypeId}/${buildId}:id${escapeFilePathForURL(
            `${parentPath}/${file}`,
          )}`}
          key={file}
          level={level}
          name={file}
          icon="file"
        />
      ))}
    </ul>
  )
}
